import {
  useWSModal,
  useWSSnackbar,
  WSActions,
  WSAlert,
  WSElement,
  WSFlexBox,
  WSPanel,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  IPayeeEngagementResponse,
  IPayeeResponse,
  PayerPayeeEngagementStatus
} from "@wingspanhq/payments/dist/interfaces";
import { WSErrorMessage } from "../../../components/WSErrorMessage/WSErrorMessage";
import { useMutationUpdatePayeeEngagement } from "../../../query/payeeEngagements/mutations/useMutationUpdatePayeeEngagement";
import { FieldView } from "../../../shared/components/FieldView/FieldView";
import React from "react";
import { wsName } from "@wingspanhq/utils/dist/name/wsName";

export interface ArchiveEngagementModalProps {
  payee: IPayeeResponse;
  engagement: IPayeeEngagementResponse;
  onClose: () => void;
}

export const ArchiveEngagementModal: React.FC<ArchiveEngagementModalProps> = ({
  payee,
  engagement,
  onClose
}) => {
  const { openSnackbar } = useWSSnackbar();
  const [updateEngagement, updateEngagementMeta] =
    useMutationUpdatePayeeEngagement();

  const payeeNames = wsName({
    user: payee.user!,
    member: payee.member
  });

  return (
    <>
      <WSText.ParagraphSm mb="L" weight="book">
        Are you sure you want to archive this assignment?
      </WSText.ParagraphSm>
      <WSPanel>
        <WSFlexBox gap="XL">
          <FieldView
            label="Assignment name"
            value={engagement.engagementName}
          />
          <FieldView
            label="Contractor name"
            value={payeeNames.getResolvedName()}
          />
        </WSFlexBox>
      </WSPanel>

      <WSAlert
        my="2XL"
        theme="info"
        icon="info-circle"
        size="M"
        title="Archiving an assignment - what to expect:"
      >
        <WSElement as="ul" ml="L">
          <li>
            No new invoices and payments can be created for this assignment.
          </li>
          <li>Deposited invoices can be accessed on the invoicing page.</li>
        </WSElement>
        You can always restore the assignment when needed.
      </WSAlert>

      <WSErrorMessage
        error={updateEngagementMeta.error}
        mb="XL"
        contextKey="ArchiveClient"
      />

      <WSActions
        alignment="fill"
        buttons={[
          {
            label: "Cancel",
            onClick: onClose,
            kind: "Secondary"
          },
          {
            label: "Archive assignment",
            loading: updateEngagementMeta.isLoading,
            onAsyncClick: async () => {
              await updateEngagement(
                {
                  payeeId: payee.payeeId,
                  engagementId: engagement.payerPayeeEngagementId,
                  status: PayerPayeeEngagementStatus.Inactive
                },
                {
                  onSuccess: () => {
                    openSnackbar({
                      theme: "dark",
                      type: "success",
                      message: "Assignment archived"
                    });
                    onClose();
                  }
                }
              );
            }
          }
        ]}
      />
    </>
  );
};

export const useArchiveEngagementModal = () => {
  return useWSModal(ArchiveEngagementModal, {
    title: "Archive assignment",
    size: "M"
  });
};
